import { useMemo } from 'react';
import { graphql, Link, useStaticQuery } from 'gatsby';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import { useQuery } from 'react-query';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import 'swiper/css/pagination';

import { formatCurrency } from '../../utils/i18n/format';
import generateWatchProjectImage from '../../utils/generateWatchProjectImage';
import Image from '../Atoms/Image';
import { getSalesWatches } from '../../utils/api';
import ArrowButton from '../Atoms/ArrowButton';
import { BUYING_PRODUCTS_ROUTE } from '../../utils/routes/const';

const productQuery = graphql`
{
  watches: allSalesWatch(limit: 3, sort: [{updatedAt: DESC}, {createdAt: ASC}]) {
    edges {
      node {
        imagePath
        watch {
          brand {
            name
          }
          model {
            name
          }
          referenceNumber
          slug
        }
        price
      }
    }
  }
}`

// TODO optimize slider
const BuyingHero = ({ bgColor = 'bg-blue', title = 'buyingHero.title', description = '', brand = '' }: { bgColor?: string, title?: string, description?: string, brand?: string, }) => {
  const { t } = useTranslation();
  const { watches } = useStaticQuery(productQuery);
  let salesWatches = [];

  if (brand) {
    const filters = useMemo(() => ({
      brandName: [brand],
      modelName: [],
      watchCondition: [],
      movement: [],
      gender: [],
      page: 1,
      limit: 3,
    }), [brand]);

    const { data } = useQuery(`sales-watches/${Object.entries(filters).join("-")}`, () => getSalesWatches(filters, 'null'))

    salesWatches = data ? data['hydra:member'] : [];
  } else {
    salesWatches = watches.edges.map(edge => edge.node);
  }

  return (
    <section className={`py-12 px-4 lg:px-8 ${bgColor} w-screen flex items-center pb-20`}>
      <div className="flex flex-col justify-center mx-auto">
        <div className="flex lg:flex-row flex-col justify-between h-full mt-20 lg:mt-40">
          <div className="lg:w-1/12 lg:block hidden" />
          <div className="w-screen lg:w-3/12 pr-32 lg:pr-7">
            <h1 className="font-cirka text-4xl lg:text-6xl text-left text-primary mb-8" >{t(title)}</h1>
            <div className="font-roboto font-light text-lg" dangerouslySetInnerHTML={{ __html: description }} />
          </div>
          <div className="md:w-7/12 md:ml-14">
            <h2 className="text-gray-400 text-lg mb-4">{t('buyingHero.subTitle')}</h2>
            <Swiper
              spaceBetween={50}
              slidesPerView="auto"
              cssMode
              className="h-80 lg:h-550"
            >
              {salesWatches.map((product) => (
                <SwiperSlide
                  tag="article"
                  className="flex !w-64 lg:!w-112 h-80 lg:h-550 relative"
                  key={`buyingHero-${product.watch.slug}-${product.price}`}
                >
                  <div className="flex flex-col w-64 lg:w-112 h-80 lg:h-550 z-10 bg-black bg-opacity-60 p-8">
                    <p className="font-cirka text-2xl lg:text-4xl text-left text-primary mb-3">{`${product.watch.brand ? product.watch.brand.name : ''} ${product.watch.model ? product.watch.model.name : ''} ${product.watch.referenceNumber}`}</p>
                    <p className="text-gray-400 text-sm lg:text-lg">{product.price !== 0 ? formatCurrency(product.price) : t('general.priceOnDemand')}</p>
                    <Link className="mt-auto no-animation" to={BUYING_PRODUCTS_ROUTE(product.watch.slug)} >
                      <ArrowButton />
                    </Link>
                  </div>
                  <Image
                    src={generateWatchProjectImage(`upload/${product.imagePath}`, product.watch.referenceNumber)}
                    alt={product.watch.referenceNumber}
                    className="w-64 lg:w-112 h-80 lg:h-550 absolute top-0 -z-10 object-cover"
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </div>
    </section>
  );
}

export default BuyingHero;

